import { useMemo } from 'react';

import { sortBy } from 'lodash';

import useUserHook from 'hooks/useUserHook';

import { PivotTableGroupDetails } from 'api/endPoints/getPivotTable';

import { UseGetPivotTableProvider } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/PivotBoard/PivotBoard.provider';
import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/PivotBoard/components/GroupByTable/GroupedByTable.module.scss';
import { getGroupedByTableColumns } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/PivotBoard/components/GroupByTable/GroupedByTable.utils';

interface Props {
    groupsData: PivotTableGroupDetails[];
    ui: UseGetPivotTableProvider['ui'];
    api: UseGetPivotTableProvider['api'];
}

export interface TreeRow extends PivotTableGroupDetails {
    subRows?: TreeRow[];
    className?: string;
}

// This function assumes only 1 depth of nested level
export const buildTreeFromFlatData = (flatDataRows?: PivotTableGroupDetails[]): TreeRow[] => {
    if (!flatDataRows) {
        return [];
    }
    const sortedFlatData: PivotTableGroupDetails[] = sortBy(flatDataRows, 'rowId');

    const treeData: TreeRow[] = [];
    let currentGroup: TreeRow | null = null;
    for (const row of sortedFlatData) {
        const node = { ...row };
        if (row.parentId === null) {
            node['className'] = '';
            currentGroup = node;
            node['subRows'] = [];
            treeData.push(node);
        } else if (currentGroup && row.parentId === currentGroup.rowId) {
            node['className'] = classes.nestedRow;
            currentGroup.subRows?.push(node);
        }
    }
    return treeData;
};

export const useGroupByTableLogic = ({ groupsData, ui, api }: Props) => {
    const { ability } = useUserHook();
    const handleAPICsvExportPrivilege = ability.can('view', 'FileDownload');

    const getExportPivotTableToCsvHandler = (title: string, subTitle: string) => {
        const exportPivotTableToCsvHandler = () => {
            const isPayloadNotEmpty = api.exportPivotTableToCsv.payload.cardCachedFilter.filter.filters.length > 0;
            if (isPayloadNotEmpty) {
                api.exportPivotTableToCsv.instance.mutate({
                    payload: api.exportPivotTableToCsv.payload,
                    fileName: `${title}_${subTitle}`,
                });
            }
        };
        return exportPivotTableToCsvHandler;
    };

    const groupedByTableColumns = useMemo(() => {
        return getGroupedByTableColumns(
            ui.state.primaryGroupSelected.label,
            ui.state.secondaryGroupSelected?.label ?? undefined,
        );
    }, [ui.state.primaryGroupSelected, ui.state.secondaryGroupSelected]);

    const groupedByTableDataTree = useMemo(() => {
        const treeData = buildTreeFromFlatData(groupsData);
        return treeData;
    }, [groupsData]);

    return {
        logic: {
            groupedByTableDataTree,
            handleAPICsvExportPrivilege,
            groupedByTableColumns,
            getExportPivotTableToCsvHandler,
        },
    };
};
